import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Briefcase, AlertTriangle, Palette, Globe2, Send, ListChecks, FileText, UserCheck } from "lucide-react";
import { Link } from "react-router-dom";
import CompanyBadge from "@/components/CompanyBadge";

const TILES = [
  { key: "open_tasks", label: "Open Tasks", icon: ListChecks, to: "/agenda", tone: "sage" },
  { key: "overdue_jobs", label: "Overdue Jobs", icon: AlertTriangle, to: "/jobs", tone: "terracotta" },
  { key: "missing_artwork", label: "Missing Artwork", icon: Palette, to: "/artwork", tone: "amber" },
  { key: "website_updates", label: "Website Updates", icon: Globe2, to: "/website", tone: "sage" },
  { key: "ready_to_send", label: "Ready to Send", icon: Send, to: "/sendoffs", tone: "sage" },
  { key: "open_quotes", label: "Open Quotes", icon: FileText, to: "/quotes", tone: "sage" },
  { key: "open_jobs", label: "Active Jobs", icon: Briefcase, to: "/jobs", tone: "sage" },
  { key: "needs_customer_approval", label: "Awaiting Approval", icon: UserCheck, to: "/jobs", tone: "amber" },
];

const toneClass = {
  sage: "text-sage",
  terracotta: "text-terracotta",
  amber: "text-amber",
};

export default function Dashboard() {
  const [stats, setStats] = useState({});
  const [recentJobs, setRecentJobs] = useState([]);
  const [recentAgenda, setRecentAgenda] = useState([]);

  const load = async () => {
    try {
      const [s, j, t] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/jobs"),
        api.get("/tasks"),
      ]);
      setStats(s.data);
      setRecentJobs((j.data || []).slice(0, 5));
      setRecentAgenda((t.data || []).filter((x) => !x.completed).slice(0, 5));
    } catch { /* noop */ }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-[1600px] mx-auto px-8 py-10">
      <PageHeader
        eyebrow="Overview"
        title="Studio Dashboard"
        description="A calm read of what's open, overdue, and ready to move today."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5" data-testid="dashboard-tiles">
        {TILES.map(({ key, label, icon: Icon, to, tone }, i) => (
          <Link to={to} key={key} data-testid={`tile-${key}`} className="group">
            <Card className="card-elev transition-transform duration-200 group-hover:-translate-y-0.5"
                  style={{ animationDelay: `${i * 40}ms` }}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="label-caps">{label}</div>
                  <Icon className={`h-4 w-4 ${toneClass[tone]}`} />
                </div>
                <div className="font-heading text-5xl mt-4 tracking-tight" data-testid={`tile-${key}-value`}>
                  {stats[key] ?? 0}
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
        <Card className="card-elev">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-2xl">Recent Jobs</h3>
              <Link to="/jobs" className="text-sm text-sage hover:underline">View all →</Link>
            </div>
            <div className="divide-y divide-border/60">
              {recentJobs.length === 0 && (
                <div className="py-6 text-sm text-muted-foreground">No jobs yet.</div>
              )}
              {recentJobs.map((j) => (
                <div key={j.id} className="py-3 flex items-center justify-between gap-4" data-testid={`recent-job-${j.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{j.job_name}</div>
                    <div className="text-xs text-muted-foreground">{j.owner || "Unassigned"} · {j.status}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <CompanyBadge name={j.company} size="sm" showLabel={false} />
                    <div className="text-xs text-muted-foreground">{j.due_date || "—"}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="card-elev">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-2xl">Today's Focus</h3>
              <Link to="/agenda" className="text-sm text-sage hover:underline">Open agenda →</Link>
            </div>
            <div className="divide-y divide-border/60">
              {recentAgenda.length === 0 && (
                <div className="py-6 text-sm text-muted-foreground">No open tasks. Add one from the Daily Agenda.</div>
              )}
              {recentAgenda.map((t) => (
                <div key={t.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">{t.title}</div>
                    <div className="text-xs text-muted-foreground">{t.owner || "Unassigned"} · {t.priority}</div>
                  </div>
                  <div className="text-xs text-muted-foreground">{t.date || "—"}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
